import { BOARD, GROUPS, LEVEL_NAMES, MAX_LEVEL } from '../game/board'
import { canUpgrade, rentFor, upgradeCostFor } from '../game/engine'
import {
  assetIncome,
  baseExpenses,
  childExpenses,
  freedomTarget,
  loanPayments,
  paydayDetail,
  passiveIncome,
  propertyIncome,
  totalExpenses,
} from '../game/finance'
import { controlsCurrent, useGame } from '../store/gameStore'
import type { GameState, Player } from '../game/types'
import { money, short, signed } from '../util/format'

interface Props {
  g: GameState
  player: Player
}

export function StatementPanel({ g, player }: Props) {
  const { upgrade, mortgage, unmortgage, dumpProperty, dumpAsset } = useGame()
  const controls = useGame(controlsCurrent)
  const isMine = controls && g.current === player.id
  const pd = paydayDetail(g, player)
  const passive = passiveIncome(g, player)
  const target = freedomTarget(g, player)
  const progress = target > 0 ? Math.min(1, passive / target) : 0
  const e = player.profession.expenses
  const owned = g.tiles
    .map((ts, id) => ({ ts, id }))
    .filter((x) => x.ts.ownerId === player.id)

  return (
    <div className="statement">
      <header className="st-head" style={{ borderColor: player.character.color }}>
        <span className="st-ava" style={{ background: player.character.color }}>
          {player.character.emoji}
        </span>
        <span className="st-id">
          <strong>{player.name}</strong>
          <span className="faint">
            {player.profession.emoji} {player.profession.name}
          </span>
        </span>
        <span className="st-cash num">{money(player.cash)}</span>
      </header>

      <div className="st-goal">
        <div className="st-goal-top">
          <span>🎯 หลุดพ้นวงจรหนู</span>
          <strong className="num">
            {money(passive)} <span className="faint">/ {money(target)}</span>
          </strong>
        </div>
        <div className="st-goal-bar">
          <i style={{ width: `${progress * 100}%` }} />
        </div>
        <span className="faint tiny">
          ทำให้ Passive Income ≥ รายจ่ายรวม แล้วชนะทันที — หรือรอให้คู่แข่งล้มละลายหมด
        </span>
      </div>

      <section className="st-block">
        <h4>รายได้</h4>
        <Row label="เงินเดือน" value={pd.salary} note={pd.salary !== player.profession.salary ? 'ถูกเอฟเฟกต์ปรับ' : undefined} />
        <Row label="กระแสเงินสดจากสินทรัพย์" value={assetIncome(player)} sub />
        <Row label="กระแสเงินสดจากโฉนด" value={propertyIncome(g, player)} sub />
        <Row label="Passive Income รวม" value={passive} strong />
      </section>

      <section className="st-block">
        <h4>รายจ่าย</h4>
        <Row label="ภาษี" value={-e.tax} sub />
        <Row label="ผ่อนบ้าน" value={-e.home} sub />
        <Row label="ผ่อนยาน" value={-e.car} sub />
        {player.profession.perkId !== 'frugal' && <Row label="บัตรเครดิต" value={-e.card} sub />}
        <Row label="อื่น ๆ" value={-e.other} sub />
        {player.children > 0 && (
          <Row label={`ค่าเลี้ยงดูบุตร (${player.children})`} value={-childExpenses(player)} sub />
        )}
        <Row label="ค่าผ่อนสินเชื่อ" value={-loanPayments(player)} sub />
        <Row label="รายจ่ายรวม" value={-totalExpenses(player)} strong />
        {baseExpenses(player) === 0 && <span className="faint tiny">—</span>}
      </section>

      <div className={`st-payday ${pd.net >= 0 ? 'good' : 'bad'}`}>
        <span>💵 Payday ต่อรอบ</span>
        <strong className="num">{signed(pd.net)}</strong>
      </div>

      {player.effects.length > 0 && (
        <section className="st-block">
          <h4>สถานะติดตัว</h4>
          <div className="eff-list">
            {player.effects.map((ef) => (
              <span key={ef.uid} className={`eff ${ef.good ? 'good' : 'bad'}`}>
                {ef.emoji} {ef.label} <b>{ef.turnsLeft}</b>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="st-block">
        <h4>
          สินทรัพย์จากดีล <span className="faint">({player.assets.length})</span>
        </h4>
        {player.assets.length === 0 && <p className="faint tiny">ยังไม่มี — ลงช่อง 💡 เพื่อจั่วการ์ดโอกาส</p>}
        {player.assets.map((a) => (
          <div key={a.uid} className="holding">
            <span className="holding-main">
              <b>
                {a.emoji} {a.name}
              </b>
              <span className="faint tiny">
                ลงทุน {short(a.cost)} · มูลค่า {short(a.value)}
                {a.units ? ` · ${a.units} หน่วย` : ''}
              </span>
            </span>
            <span className={`holding-cf num ${a.cashflow > 0 ? 'good-text' : 'faint'}`}>
              {a.cashflow > 0 ? `+${short(a.cashflow)}/ด.` : 'ไม่ปันผล'}
            </span>
            {isMine && (
              <button className="btn btn-sm btn-ghost" onClick={() => dumpAsset(a.uid)}>
                ขาย
              </button>
            )}
          </div>
        ))}
      </section>

      <section className="st-block">
        <h4>
          โฉนดบนกระดาน <span className="faint">({owned.length})</span>
        </h4>
        {owned.length === 0 && <p className="faint tiny">ยังไม่มีโฉนด</p>}
        {owned.map(({ id, ts }) => {
          const tile = BOARD[id]
          const group = tile.group ? GROUPS[tile.group] : null
          const canUp = isMine && canUpgrade(g, player, id)
          return (
            <div key={id} className={`holding${ts.mortgaged ? ' mortgaged' : ''}`}>
              {group && <span className="holding-chip" style={{ background: group.color }} />}
              <span className="holding-main">
                <b>
                  {tile.emoji} {tile.name}
                </b>
                <span className="faint tiny">
                  {ts.mortgaged ? '🏚️ จำนองอยู่ · ' : ''}
                  {tile.kind === 'property' ? LEVEL_NAMES[ts.level] : tile.kind === 'transport' ? 'ท่าขนส่ง' : 'สาธารณูปโภค'}
                  {' · ค่าเช่า '}
                  {short(rentFor(g, id, 7))}
                </span>
              </span>
              <span className="holding-cf num good-text">
                +{short(tile.monthly?.[Math.min(ts.level, (tile.monthly?.length ?? 1) - 1)] ?? 0)}/ด.
              </span>
              {isMine && (
                <span className="holding-actions">
                  {tile.kind === 'property' && ts.level < MAX_LEVEL && (
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={!canUp}
                      onClick={() => upgrade(id)}
                      title={
                        canUp
                          ? `อัปเกรด ${money(upgradeCostFor(player, tile))}`
                          : 'ต้องถือครบทั้งกลุ่มสี อัปเกรดไล่ระดับ และมีเงินพอ'
                      }
                    >
                      🏗️ {short(upgradeCostFor(player, tile))}
                    </button>
                  )}
                  {ts.mortgaged ? (
                    <button className="btn btn-sm btn-ghost" onClick={() => unmortgage(id)}>
                      ไถ่ถอน
                    </button>
                  ) : (
                    <button className="btn btn-sm btn-ghost" onClick={() => mortgage(id)}>
                      จำนอง
                    </button>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={() => dumpProperty(id)}>
                    ขาย
                  </button>
                </span>
              )}
            </div>
          )
        })}
      </section>

      {player.liabilities.length > 0 && (
        <section className="st-block">
          <h4>
            หนี้สิน <span className="faint">({player.liabilities.length})</span>
          </h4>
          {player.liabilities.map((l) => (
            <div key={l.uid} className="holding">
              <span className="holding-main">
                <b>
                  {l.emoji} {l.name}
                </b>
                <span className="faint tiny">คงเหลือ {short(l.balance)}</span>
              </span>
              <span className="holding-cf num bad-text">
                {l.monthly > 0 ? `−${short(l.monthly)}/ด.` : 'หักในกระแสเงินสดแล้ว'}
              </span>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function Row({
  label,
  value,
  sub,
  strong,
  note,
}: {
  label: string
  value: number
  sub?: boolean
  strong?: boolean
  note?: string
}) {
  return (
    <div className={`st-row${sub ? ' sub' : ''}${strong ? ' strong' : ''}`}>
      <span>
        {label}
        {note && <em className="faint tiny"> · {note}</em>}
      </span>
      <span className={`num ${value < 0 ? 'bad-text' : value > 0 ? 'good-text' : 'faint'}`}>
        {money(value)}
      </span>
    </div>
  )
}
